export interface ClarificationOptionPayload {
  readonly label: string;
  readonly value: string;
}

export interface ClarificationQuestionPayload {
  readonly id: string;
  readonly mode: "CONFIRMATION" | "SELECTION" | "MULTI_SELECTION";
  readonly question: string;
  readonly slotType: string;
  readonly searchTerm?: string;
  readonly proposedPath?: string;
  readonly options: readonly ClarificationOptionPayload[];
}

export const SUBAGENT_RESULT_KINDS = [
  "activation-condition",
  "activation-condition-explanation",
  "rule",
  "rule-explanation",
  "email",
  "email-explanation",
  "auto-state-change",
  "create-task",
  "update-task",
  "move-reorder",
  "delete-task",
  "create-area",
  "create-phase",
] as const;

export type SubagentResultKind = (typeof SUBAGENT_RESULT_KINDS)[number];

// What a Process AI vote rates: the result card's kind, or "clarification" for a question card. A text reply has none.
export const PROCESS_AGENT_FEEDBACK_KINDS = [...SUBAGENT_RESULT_KINDS, "clarification"] as const;

export type ProcessAgentFeedbackKind = (typeof PROCESS_AGENT_FEEDBACK_KINDS)[number];

export type SubagentTargetSection =
  | "general"
  | "task"
  | "email"
  | "reminders"
  | "activation"
  | "rules"
  | "duration"
  | "autoStateChange";

export type SubagentTargetScope = "task" | "process";

export type SubagentErrorKind = "user-decline" | "technical-error";

export type ProcessAgentStreamEvent =
  | {
      readonly type: "progress";
      readonly data: { readonly phase: string; readonly iteration?: number; readonly detail?: string };
    }
  | {
      readonly type: "tool:start";
      readonly data: {
        readonly toolCallId: string;
        readonly name: string;
        readonly args?: Record<string, unknown>;
        readonly seq?: number;
      };
    }
  | {
      readonly type: "tool:batch:start";
      readonly data: { readonly batchId: string; readonly expectedSize: number; readonly seq?: number };
    }
  | {
      readonly type: "tool:done";
      readonly data: {
        readonly toolCallId: string;
        readonly name: string;
        readonly durationMs: number;
        readonly seq?: number;
      };
    }
  | {
      readonly type: "tool:error";
      readonly data: {
        readonly toolCallId: string;
        readonly name: string;
        readonly error: string;
        readonly seq?: number;
      };
    }
  | { readonly type: "subagent:start"; readonly data: { readonly subagentName: string; readonly subagentId: string } }
  | {
      readonly type: "subagent:progress";
      readonly data: {
        readonly subagentName: string;
        readonly subagentId: string;
        readonly phase: string;
        readonly detail?: string;
      };
    }
  | {
      readonly type: "subagent:result";
      readonly data: {
        readonly subagentName: string;
        readonly subagentId: string;
        readonly subagentInvocationId: string;
        readonly kind: SubagentResultKind;
        readonly targetTaskId: string | null;
        readonly targetSection: SubagentTargetSection;
        readonly targetScope: SubagentTargetScope;
        readonly targetItemId?: string;
        readonly ok: boolean;
        readonly result?: unknown;
        readonly error?: string;
        readonly errorKind?: SubagentErrorKind;
        readonly taskName?: string;
        readonly batchId?: string;
      };
    }
  | {
      readonly type: "subagent:applied";
      readonly data: {
        readonly subagentInvocationId: string;
        readonly kind: SubagentResultKind;
        readonly targetTaskId: string | null;
        readonly targetSection: SubagentTargetSection;
        readonly targetScope: SubagentTargetScope;
        readonly targetItemId?: string;
        readonly taskName: string;
        readonly commandId?: string;
      };
    }
  | {
      readonly type: "clarification:required";
      readonly data: {
        readonly subagentName: string;
        readonly subagentId: string;
        readonly taskId: string | null;
        readonly targetScope?: SubagentTargetScope;
        readonly targetItemId?: string;
        readonly originalDescription: string;
        readonly questions: readonly ClarificationQuestionPayload[];
        readonly batchId?: string;
        readonly taskName?: string;
      };
    }
  | {
      readonly type: "model:switch";
      readonly data: { readonly from: string; readonly to: string; readonly reason: string; readonly attempt: number };
    }
  | { readonly type: "message"; readonly data: { readonly text: string } }
  | { readonly type: "message:delta"; readonly data: { readonly messageId: string; readonly delta: string } }
  | { readonly type: "message:thinking-finalized"; readonly data: { readonly messageId: string } }
  | { readonly type: "message:answer-finalized"; readonly data: { readonly messageId: string } }
  | { readonly type: "finish"; readonly data: { readonly message?: string; readonly traceId?: string } };

export const KNOWN_STREAM_EVENT_TYPES: ReadonlySet<ProcessAgentStreamEvent["type"]> = new Set<
  ProcessAgentStreamEvent["type"]
>([
  "progress",
  "tool:start",
  "tool:batch:start",
  "tool:done",
  "tool:error",
  "subagent:start",
  "subagent:progress",
  "subagent:result",
  "subagent:applied",
  "clarification:required",
  "model:switch",
  "message",
  "message:delta",
  "message:thinking-finalized",
  "message:answer-finalized",
  "finish",
]);

export function isValidStreamEventType(t: string): t is ProcessAgentStreamEvent["type"] {
  return KNOWN_STREAM_EVENT_TYPES.has(t as ProcessAgentStreamEvent["type"]);
}
